import { useGetMyProfile, useGetMyUpdates } from "@workspace/api-client-react";
import { Link } from "wouter";
import Navbar from "@/components/layout/Navbar";
import { Button } from "@workspace/ui-web/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@workspace/ui-web/components/ui/card";
import {
  Loader2,
  PlusCircle,
  Dog,
  Calendar as CalendarIcon,
  FileText,
  Image as ImageIcon,
  Target,
} from "lucide-react";
import { format } from "date-fns";

export default function PortalHome() {
  const { data: profile, isLoading: profileLoading } = useGetMyProfile();
  const { data: updates, isLoading: updatesLoading } = useGetMyUpdates();

  if (profileLoading || updatesLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-muted/20">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-muted/20">
      <Navbar />
      <main className="flex-1 container px-4 md:px-8 py-8 mx-auto max-w-5xl">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Raiser Portal</h1>
            <p className="text-muted-foreground">
              Document your dog's journey and milestones.
            </p>
          </div>
          <div className="flex gap-3">
            {!profile ? (
              <Link href="/portal/profile">
                <Button>Create Profile</Button>
              </Link>
            ) : (
              <>
                <Link href="/portal/profile">
                  <Button variant="outline">Edit Profile</Button>
                </Link>
                <Link href="/portal/updates/new">
                  <Button className="gap-2">
                    <PlusCircle className="h-4 w-4" /> New Update
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>

        {!profile ? (
          <Card className="bg-accent/20 border-accent text-center py-12">
            <CardContent className="space-y-4">
              <div className="mx-auto w-16 h-16 bg-background rounded-full flex items-center justify-center shadow-sm">
                <Dog className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-xl font-semibold">
                Welcome to Greater Hope!
              </h2>
              <p className="text-muted-foreground max-w-md mx-auto">
                Before you can start submitting updates, please create your
                raiser profile and tell us about your dog.
              </p>
              <Link href="/portal/profile">
                <Button size="lg" className="mt-4">
                  Set Up Profile
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-[1fr_2fr] gap-8">
            {/* Sidebar Profile Summary */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Dog className="h-5 w-5 text-primary" />
                    {profile.dogName || "My Dog"}
                  </CardTitle>
                  <CardDescription>
                    {profile.dogBreed || "Breed not specified"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  {profile.startedAt && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <CalendarIcon className="h-4 w-4" /> Started{" "}
                      {format(new Date(profile.startedAt), "MMM d, yyyy")}
                    </div>
                  )}
                  {profile.location && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <span className="font-medium text-foreground">
                        Location:
                      </span>{" "}
                      {profile.location}
                    </div>
                  )}
                  {profile.bio && (
                    <div className="pt-2 border-t mt-4">
                      <p className="text-muted-foreground italic">
                        "{profile.bio}"
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-primary text-primary-foreground border-none">
                <CardContent className="p-6 text-center space-y-2">
                  <div className="text-4xl font-bold">
                    {updates?.length || 0}
                  </div>
                  <div className="text-primary-foreground/80 font-medium">
                    Total Updates
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Updates Feed */}
            <div className="space-y-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <FileText className="h-5 w-5" /> Update History
              </h2>

              {!updates?.length ? (
                <div className="text-center py-16 border rounded-xl bg-card border-dashed">
                  <p className="text-muted-foreground mb-4">
                    No updates recorded yet.
                  </p>
                  <Link href="/portal/updates/new">
                    <Button variant="outline">Log your first update</Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {updates.map((update) => (
                    <Link key={update.id} href={`/portal/updates/${update.id}`}>
                      <Card className="hover:border-primary/50 transition-colors cursor-pointer group">
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div className="font-medium text-primary">
                              {format(
                                new Date(update.createdAt),
                                "MMMM d, yyyy",
                              )}
                            </div>
                            {update.weightLbs && (
                              <div className="text-sm bg-muted px-2 py-1 rounded-md font-medium">
                                {update.weightLbs} lbs
                              </div>
                            )}
                          </div>
                          <p className="text-foreground line-clamp-2">
                            {update.note}
                          </p>

                          <div className="flex gap-4 mt-4 text-sm text-muted-foreground">
                            {update.photos.length > 0 && (
                              <span className="flex items-center gap-1">
                                <ImageIcon className="h-4 w-4" />{" "}
                                {update.photos.length} photo
                                {update.photos.length !== 1 ? "s" : ""}
                              </span>
                            )}
                            {update.milestones.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Target className="h-4 w-4" />{" "}
                                {update.milestones.length} milestone
                                {update.milestones.length !== 1 ? "s" : ""}
                              </span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
